import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Product from "./Card";
import { useCart } from "../../context/CartContext";
import productAPI from "../../api/productApi";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import KeyFeatures from "./detailfeatures/KeyFeatures";
import ProductCard from "./Card";
import { Col, Row } from "react-bootstrap";
import { Download, Eye, Heart, View } from "lucide-react";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { showError } from "../../utils/toastUtils";

function DetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);

  const thumbRef = useRef();

  const [product, setProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [displayedImages, setDisplayedImages] = useState([]);

  const [mainImage, setMainImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const [showScrollIcon, setShowScrollIcon] = useState(false);

  const [viewFixVideo, setViewFixVideo] = useState(false);

  const [title, setTitle] = useState();
  const [brand, setBrand] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [category, setCategory] = useState({});
  const [description, setDescription] = useState("");
  const [stock, setStock] = useState("");

  /******************* FETCH PRODUCT *******************/
  const fetchProductDetail = async () => {
    try {
      const res = await productAPI.details(slug);

      if (res.code === 200) {
        const {
          product,
          detail,
          brand,
          category,
          variants,
          relatedProducts,
          isWishlisted,
        } = res.data;

        setProduct(product);

        setIsWishlisted(isWishlisted || false);

        setDetail(detail);
        setBrand(brand);
        setCategory(category);
        setVariants(variants || []);
        setRelatedProducts(relatedProducts || []);

        setTitle(product?.title);
        setSkuCode(product?.skuCode);
        setDescription(detail?.description);

        // default variant
        if (variants?.length > 0) {
          setSelectedVariant(variants[0]);
        }

        // images
        const images = [];
        if (product?.thumbnail) {
          images.push(product.thumbnail);
        }

        if (detail?.images?.length) {
          images.push(...detail.images);
        }

        if (variants?.length) {
          variants.forEach((v) => {
            if (v.images?.length) {
              images.push(...v.images);
            }
          });
        }

        if (images.length > 0) {
          setMainImage(images[0]);
          setDisplayedImages(images);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [slug]);

  /******************* IMAGES *******************/
  const mergedImages = [
    ...(product?.thumbnail ? [product.thumbnail] : []),
    ...(detail?.images?.map((i) => i) || []),
    ...variants.flatMap((v) => v.images || []),
  ];

  /******************* VARIANT ATTRIBUTES *******************/
  const attributeMap = {};

  variants.forEach((variant) => {
    Object.entries(variant.attributes || {}).forEach(([key, value]) => {
      if (!attributeMap[key]) {
        attributeMap[key] = new Set();
      }

      attributeMap[key].add(value);
    });
  });

  const handleVariantClick = (key, value) => {
    const updated = {
      ...selectedAttributes,
      [key]: value,
    };

    setSelectedAttributes(updated);

    const matched = variants.find((v) =>
      Object.entries(updated).every(([k, val]) => v.attributes?.[k] === val),
    );

    if (matched) {
      setSelectedVariant(matched);

      if (matched.images?.length) {
        const variantImgs = matched.images;

        setDisplayedImages(variantImgs);
        setMainImage(variantImgs[0]);
      }
    }
  };
  const handleDownloadBrochure = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop() || "brochure.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Brochure download failed:", error);
    }
  };
  // Create attribute map
  if (variants) {
    variants.forEach((variant) => {
      if (variant.attributes && typeof variant.attributes === "object") {
        Object.entries(variant.attributes).forEach(([key, value]) => {
          if (!attributeMap[key]) {
            attributeMap[key] = new Set();
          }
          attributeMap[key].add(value);
        });
      }
    });
  }

  // Set default attributes on mount
  useEffect(() => {
    const defaultSelected = {};
    Object.entries(attributeMap).forEach(([key, valueSet]) => {
      const values = Array.from(valueSet);
      if (values.length > 0) {
        defaultSelected[key] = values[0];
      }
    });

    setSelectedAttributes(defaultSelected);
  }, []);

  const extractYouTubeID = (url) => {
    const regExp = /(?:youtube\.com.*(?:\?|&)v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // to fix bottom
  useEffect(() => {
    const handleScroll = () => {
      const quantityEl = document.querySelector(".action-btn-detail");
      const stickyEl = document.querySelector(".tf-sticky-btn-atc");
      const floatingVideo = document.querySelector(".floating-animate");

      if (!quantityEl || !stickyEl || !floatingVideo) return;

      const quantityBottom = quantityEl.getBoundingClientRect().bottom;
      const windowHeight = window.innerHeight;

      if (quantityBottom <= windowHeight) {
        stickyEl.classList.remove("show");
        floatingVideo.classList.add("active");
      } else {
        stickyEl.classList.add("show");
        floatingVideo.classList.remove("active");
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;
      if (el && el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preSelectedAttributes = {};

    for (const [key, value] of params.entries()) {
      preSelectedAttributes[key] = value;
    }

    if (Object.keys(preSelectedAttributes).length > 0 && variants.length > 0) {
      setSelectedAttributes(preSelectedAttributes);

      const matched = variants.find((variant) =>
        Object.entries(preSelectedAttributes).every(
          ([key, val]) => variant.attributes?.[key] === val,
        ),
      );

      if (matched) {
        setSelectedVariant(matched);
        setTitle(`${product?.title} - ${matched.variant_name}`);
        setSkuCode(matched.sku || product?.skuCode);
        setDescription(matched.description || detail?.description);

        const variantDetailImages = matched.images
          ? matched.images.map((img) => img)
          : [];

        const mergedImages = [...variantDetailImages];

        if (mergedImages.length > 0) {
          setMainImage(mergedImages[0]);
        }
      }
    }
  }, [variants]);

  /******************* CART *******************/
  const handleAddToCart = (e) => {
    const action = e.currentTarget.dataset.action;

    try {
      addToCart(product._id, quantity, selectedVariant?._id);

      if (action === "buy-now") {
        navigate("/checkout");
      }
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  /******************* THUMB SCROLL *******************/

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;

      if (!el) return;

      if (el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();

    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  /******************* LOADING *******************/
  if (!product) return <div className="container mt-5">Loading...</div>;

  /************* FUNCTIONS FOR DEFINE*****************/
  const price = selectedVariant
    ? selectedVariant.price
    : product?.productType === "Variant"
      ? detail?.pricing?.minPrice
      : detail?.pricing?.minPrice;

  const inStock = selectedVariant ? Number(selectedVariant.stock) > 0 : true;

  // brochure / PDF url coming from backend — adjust the field name below
  // if your API sends it under a different key (e.g. product?.brochureUrl)
  const brochureUrl = detail?.brochureUrl;

  const handleWishlistToggle = async (e) => {
    e.preventDefault();

    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: product?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  return (
    <>
      <Breadcrump title={product?.title} />
      <SEO meta={detail?.meta} />

      <section className="detail-page mt-5">
        <div className="tf-main-product section-image-zoom">
          <div className="container">
            <div className="row  gx-md-4 align-items-start">
              <div className="col-md-7">
                <div className="custom-left-sticky">
                  <div className="editorial-gallery">
                    {/* Main big image */}
                    <a
                      href={mainImage}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="main-image-wrap"
                    >
                      <img
                        src={mainImage || displayedImages[0]}
                        alt={product?.title}
                        className="main-image"
                      />
                    </a>

                    {/* Thumbnails below */}
                    {displayedImages.length > 1 && (
                      <div className="thumbnail-row" ref={thumbRef}>
                        {displayedImages.map((img, index) => (
                          <button
                            type="button"
                            key={index}
                            className={`thumb-item ${mainImage === img ? "active" : ""}`}
                            onClick={() => setMainImage(img)}
                          >
                            <img src={img} alt={`Thumb-${index}`} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-5">
                <div className="tf-product-heading">
                  {brand && (
                    <span>
                      <Link
                        to={`/brands/${brand?.slug}`}
                        className="text-decoration-none brand-tag"
                      >
                        {brand?.name}
                      </Link>
                    </span>
                  )}

                  <h5 className="product-display-title">{title}</h5>

                  {/* variant wise amount showing */}
                  <div>
                    {selectedVariant ? (
                      <>
                        <div className="product-price">
                          ₹{selectedVariant?.price?.toFixed(2)}
                        </div>
                        {selectedVariant?.mrp &&
                          selectedVariant?.mrp > selectedVariant.price && (
                            <>
                              <div className="price-tag mb-2  ">
                                ₹{selectedVariant?.mrp?.toFixed(2)}
                              </div>
                              <span className="badge-sale">
                                (
                                {Math.round(
                                  ((selectedVariant.mrp -
                                    selectedVariant.price) /
                                    selectedVariant.mrp) *
                                    100,
                                )}
                                % Off)
                              </span>
                            </>
                          )}
                      </>
                    ) : (
                      <div className="display-sm price-new">₹{price}</div>
                    )}
                  </div>
                  {/* variant wise amount section end */}

                  {(product.skuCode || category) && (
                    <div className="item">
                      <ul>
                        {product.skuCode && (
                          <li>
                            <b>SKU:</b> {product.skuCode}
                          </li>
                        )}
                        {category && (
                          <li>
                            <b>Category:</b>{" "}
                            <Link
                              to={`/categories/${category?.slug}`}
                              className="text-decoration-none text-muted"
                            >
                              {category.name}
                            </Link>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* stock availablity */}
                  <div className="product-stock">
                    {selectedVariant && selectedVariant.stock > 0 ? (
                      <span className="stock in-stock">In Stock</span>
                    ) : (
                      <span className="stock out-stock">Out of Stock</span>
                    )}
                  </div>

                  {/* variant stock hurry */}
                  {selectedVariant && selectedVariant.stock <= 100 && (
                    <div className="product-progress-sale">
                      <div className="title-hurry-up">
                        <span className="text-danger fw-medium">HURRY UP!</span>{" "}
                        Only{" "}
                        <span className="count">{selectedVariant.stock}</span>{" "}
                        items left!
                      </div>
                    </div>
                  )}

                  <hr />

                  {detail?.shortDescription && (
                    <p className="description-text">
                      {detail?.shortDescription}
                    </p>
                  )}

                  <hr />
                </div>
                <div className="tf-product-variant">
                  {Object.entries(attributeMap).map(([attrKey, values]) => {
                    const valueArray = [...values];

                    return (
                      <div
                        className="variant-size variant-picker-item"
                        key={attrKey}
                      >
                        <div className="variant-picker-label">
                          {attrKey.replace("variantAttribute", "Option")}:{" "}
                          <span className="variant-picker-label-value">
                            {selectedAttributes[attrKey] || "Select"}
                          </span>
                        </div>

                        <div className="variant-picker-values">
                          {valueArray.map((value, index) => {
                            const inputId = `${attrKey}-${value}-${index}`;
                            return (
                              <label
                                key={value}
                                htmlFor={inputId}
                                className={`variant-btn check-ship ${
                                  selectedAttributes[attrKey] === value
                                    ? "active"
                                    : ""
                                }`}
                              >
                                <input
                                  type="radio"
                                  id={inputId}
                                  name={attrKey}
                                  className="tf-check-rounded"
                                  value={value}
                                  checked={
                                    selectedAttributes[attrKey] === value
                                  }
                                  onChange={() =>
                                    handleVariantClick(attrKey, value)
                                  }
                                />
                                <span className="text text-sm">
                                  <span>{value}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="tf-product-total-quantity ">
                  <div className="group-btn d-flex gap-2 align-items-center">
                    <div className="custom-qty-wrapper">
                      <button
                        className="custom-qty-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          if (quantity > 1) setQuantity(quantity - 1);
                        }}
                      >
                        -
                      </button>
                      <input
                        className="custom-qty-input"
                        type="text"
                        name="number"
                        value={quantity}
                        readOnly
                      />
                      <button
                        className="custom-qty-btn"
                        onClick={(e) => setQuantity(quantity + 1)}
                      >
                        +
                      </button>
                    </div>

                    <button
                      type="button"
                      className={`detail-wishlist-btn ${isWishlisted ? "active" : ""}`}
                      onClick={handleWishlistToggle}
                      aria-label="Add to wishlist"
                    >
                      <Heart size={24} className="detail-wishlist-icon me-2" />
                      Wishlist
                    </button>

                    {detail?.banners?.[0] && (
                      <a
                        href={detail.banners[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="detail-brochure-btn"
                        aria-label="Download brochure"
                        style={{ textDecoration: "none" }}
                      >
                        <Eye size={24} className="detail-brochure-icon me-2" />
                        PDF
                      </a>
                    )}
                  </div>
                  <div className="action-stack mt-4">
                    <a
                      href="#shoppingCartCanvas"
                      data-bs-toggle="offcanvas"
                      className="btn btn-dark btn-detail text-decoration-none w-100 mt-2 cart-btn"
                      onClick={handleAddToCart}
                      data-action="cart"
                    >
                      Add to cart
                    </a>
                    <a
                      href="#"
                      className=" btn btn-outline-dark btn-detail text-decoration-none w-100 mt-2 buy-now"
                      onClick={handleAddToCart}
                      data-action="buy-now"
                    >
                      Buy it now
                    </a>
                  </div>
                </div>

                {detail.video && viewFixVideo && (
                  <>
                    {detail.video.includes("youtube.com") ||
                    detail.video.includes("youtu.be") ? (
                      <iframe
                        width="100%"
                        height="450"
                        src={`https://www.youtube.com/embed/${extractYouTubeID(
                          detail.video,
                        )}`}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title="YouTube video"
                      ></iframe>
                    ) : (
                      <video width="100%" height="450" controls autoPlay muted>
                        <source src={detail.video} type="video/mp4" />
                        Your browser does not support HTML5 video.
                      </video>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pf-wrap">
        <div className="container">
          <h2 className="custom-feature-heading">Product highlights</h2>

          {detail?.keyFeatures?.length > 0 && (
            <div className="pf-card">
              {detail.keyFeatures.map((feature, index) => (
                <div className="pf-row" key={index}>
                  <span className="pf-label">{feature?.label}</span>
                  <span className="pf-value">{feature?.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* <section className="custom-features-section">
        <div className="container">
          <h2 className="custom-features-heading">Product Highlights</h2>

          {detail?.keyFeatures?.length > 0 && (
            <div className="custom-features-list">
              {detail.keyFeatures.map((feature, index) => (
                <div className="custom-feature-item" key={index}>
                  <span className="custom-feature-label">{feature?.label}</span>

                  <span className="custom-feature-value">{feature?.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section> */}
      <section className="custom-desc-section mb-md-5 mb-3">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="custom-feature-heading">Product Description</h2>
              <div
                className="custom-desc-content"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </div>
        </div>
      </section>

      {relatedProducts && relatedProducts.length > 0 && (
        <div className="related-products-section bg-light py-5">
          <div className="container product">
            <div className="flat-animate-tab flat-controltab-nav">
              <div className="text-start mb-4">
                <h4 className="section-title">Related Products</h4>
              </div>
              <div className="row">
                {relatedProducts.map((product, index) => (
                  <div
                    className="col-lg-3 col-sm-6 col-xs-6 mb-2"
                    key={product?._id || index}
                  >
                    <Product item={product} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DetailPage;
